import React, { useState, forwardRef, useMemo, useCallback } from 'react';
import {
  Grid,
  Button,
  Box,
  Typography,
  Alert,
  TextField,
  Divider
} from '@mui/material';
import ModelDialog from './ModelDialog';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import ModelCard from './ModelCard';
import { 
  useGlobalSettingState, 
  useComponentWebSocket,
  textModelOptions,
  audioModelOptions,
  visionModelOptions,
  resourceModels,
  noTokenTextModels,
  providerIcons,
  tunableProviders
} from './GlobalSettingUtils';
import { deleteModel, saveModel } from '../../Utils/adminSettingsApi';

const DEFAULT_REASONING_EFFORT = 'none';

const getModelContextTokens = (model) => {
  const tokenValue = Number(model?.max_context_tokens);
  if (Number.isFinite(tokenValue) && tokenValue > 0) return tokenValue;

  const legacyCharacterValue = Number(model?.max_context_chars);
  if (Number.isFinite(legacyCharacterValue) && legacyCharacterValue > 0) {
    return Math.max(Math.floor(legacyCharacterValue / 4), 1);
  }

  return 128000;
};

const ModelManagement = forwardRef((props, ref) => {
  const { savedModels, onModelsUpdate, allowedModelProviders = [] } = props;
  const { 
    t,
    openSnackbar
  } = useGlobalSettingState();

  const allowedProviderSet = useMemo(() => new Set((allowedModelProviders || []).map((p) => (p ? String(p).toLowerCase() : '')).filter(Boolean)), [allowedModelProviders]);

  const filterOptionsByAllowance = useCallback((options) => {
    if (!allowedProviderSet.size) return options;
    return options.filter((opt) => opt.value === 'none' || allowedProviderSet.has(opt.value.toLowerCase()));
  }, [allowedProviderSet]);

  const providerOptionsByType = useMemo(() => ({
    text: filterOptionsByAllowance(textModelOptions),
    vision: filterOptionsByAllowance(visionModelOptions),
    audio: filterOptionsByAllowance(audioModelOptions)
  }), [filterOptionsByAllowance]);

  const getProviderOptionsForType = useCallback((modelType) => providerOptionsByType[modelType] || textModelOptions, [providerOptionsByType]);
  const getDefaultProviderForType = useCallback((modelType) => {
    const options = getProviderOptionsForType(modelType).filter((opt) => opt.value !== 'none');
    return options[0]?.value || '';
  }, [getProviderOptionsForType]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingModel, setEditingModel] = useState(null);
  const [performanceDefaults, setPerformanceDefaults] = useState({
    max_tokens: 60000,
    temperature: 0.1
  });
  const [performanceDirty, setPerformanceDirty] = useState(false);
  const performanceBaseline = React.useRef(performanceDefaults);
  const [formData, setFormData] = useState({
    id: null,
    provider: getDefaultProviderForType('text'),
    model_type: 'text',
    model_name: '',
    openrouter_provider: '',
    api_token: '',
    api_endpoint: '',
    api_secret: '',
    has_api_token: false,
    has_api_secret: false,
    api_region: '',
    resource_name: '',
    max_tokens: 60000,
    temperature: 0.1,
    reasoning_effort: DEFAULT_REASONING_EFFORT,
    logprobs_enabled: true,
    max_context_tokens: 128000,
    timeout: 360
  });
  const [isSavingModel, setIsSavingModel] = useState(false);

  // Performance settings remain on the existing HTTP-bridged read/config path.
  const commandHandlers = useMemo(() => ({
    'performance_settings': (message) => {
      if (!message?.data) return;
      setPerformanceDefaults((prev) => {
        const next = {
          ...prev,
          ...message.data
        };
        performanceBaseline.current = next;
        setPerformanceDirty(false);
        return next;
      });
    }
  }), []);
  
  // Use our custom WebSocket hook
  const { sendCommand } = useComponentWebSocket(commandHandlers);
  React.useEffect(() => {
    sendCommand('get_performance_settings');
  }, [sendCommand]);

  const handleOpenDialog = (model = null, modelType = 'text') => {
    if (model) {
      setEditingModel(model);
      setFormData({
        id: model.id || null,
        provider: model.provider,
        model_type: modelType,
        model_name: model.model_name || '',
        openrouter_provider: model.openrouter_provider || '',
        api_token: model.has_api_token ? '***' : '',
        api_endpoint: model.api_endpoint || '',
        api_secret: '',
        has_api_token: Boolean(model.has_api_token),
        has_api_secret: Boolean(model.has_api_secret),
        api_region: model.api_region || '',
        resource_name: model.resource_name || '',
        max_tokens: model.max_tokens ?? 60000,
        temperature: model.temperature ?? 0.1,
        reasoning_effort: model.reasoning_effort || DEFAULT_REASONING_EFFORT,
        logprobs_enabled: model.logprobs_enabled !== false && model.logprobs_enabled !== 0,
        max_context_tokens: getModelContextTokens(model),
        timeout: model.timeout ?? 360
      });
    } else {
      setEditingModel(null);
      setFormData({
        id: null,
        provider: getDefaultProviderForType(modelType),
        model_type: modelType,
        model_name: '',
        openrouter_provider: '',
        api_token: '',
        api_endpoint: '',
        api_secret: '',
        has_api_token: false,
        has_api_secret: false,
        api_region: '',
        resource_name: '',
        max_tokens: 60000,
        temperature: 0.1,
        reasoning_effort: DEFAULT_REASONING_EFFORT,
        logprobs_enabled: true,
        max_context_tokens: 128000,
        timeout: 360
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsSavingModel(false);
    setDialogOpen(false);
    setEditingModel(null);
  };

  const handleFormChange = (field) => (event) => {
    const newValue = field === 'logprobs_enabled' ? event.target.checked : event.target.value;
    setFormData(prev => {
      const updated = { ...prev, [field]: newValue };
      // When provider changes to dataunfold, set default model_name to 'high'
      if (field === 'provider' && ['dataunfold', 'dataunfold_widget'].includes(newValue) && !prev.model_name) {
        updated.model_name = 'high';
      }
      if (field === 'provider' && newValue !== 'openrouter') {
        updated.openrouter_provider = '';
      }
      // When provider changes away from dataunfold, clear tier-based model_name
      if (field === 'provider' && ['dataunfold', 'dataunfold_widget'].includes(prev.provider) && !['dataunfold', 'dataunfold_widget'].includes(newValue)) {
        if (['low', 'medium', 'high'].includes(prev.model_name)) {
          updated.model_name = '';
        }
      }
      return updated;
    });
  };

  const handleSaveModel = async () => {
    if (isSavingModel) return;
    setIsSavingModel(true);
    const parsedContext = parseInt(formData.max_context_tokens, 10);
    const maxContextValue = Number.isFinite(parsedContext) && parsedContext > 0 ? parsedContext : 128000;
    const parsedMaxTokens = parseInt(formData.max_tokens, 10);
    const maxTokensValue = Number.isFinite(parsedMaxTokens) && parsedMaxTokens > 0 ? parsedMaxTokens : 60000;
    const parsedTemperature = parseFloat(formData.temperature);
    const temperatureValue = Number.isFinite(parsedTemperature) ? parsedTemperature : 0.1;
    // For dataunfold, default model_name to 'high' if not set
    const modelName = ['dataunfold', 'dataunfold_widget'].includes(formData.provider) && !formData.model_name ? 'high' : formData.model_name;
    const payload = {
      id: formData.id,
      provider: formData.provider,
      model_type: formData.model_type,
      model_name: modelName,
      openrouter_provider: formData.openrouter_provider || '',
      api_token: formData.api_token,
      api_endpoint: formData.api_endpoint,
      api_secret: formData.api_secret,
      api_region: formData.api_region,
      resource_name: formData.resource_name,
      max_context_tokens: maxContextValue,
      max_tokens: maxTokensValue,
      temperature: temperatureValue,
      reasoning_effort: formData.reasoning_effort || DEFAULT_REASONING_EFFORT,
      logprobs_enabled: Boolean(formData.logprobs_enabled),
      timeout: parseInt(formData.timeout, 10) || 360
    };
    if (formData.api_token === '***' && formData.has_api_token) {
      delete payload.api_token;
    }
    if (formData.api_secret === '***' && formData.has_api_secret) {
      delete payload.api_secret;
    }
    const nameLower = (formData.model_name || '').toLowerCase();
    const temperatureAllowed = !(formData.provider === 'openai' && (nameLower.startsWith('o1') || nameLower.startsWith('o3')));

    if (tunableProviders.has(formData.provider)) {
      payload.max_tokens = maxTokensValue;
      if (temperatureAllowed) {
        payload.temperature = temperatureValue;
      }
    }
    try {
      const response = await saveModel(payload);
      if (response?.status !== 'success' && response?.success !== true) {
        throw new Error(response?.message || t('global_setting.model_operation_error', 'Error performing operation'));
      }
      setIsSavingModel(false);
      openSnackbar(t('global_setting.model_added_success', 'Model added successfully'), 3000);
      setDialogOpen(false);
      setFormData({
        id: null,
        provider: getDefaultProviderForType('text'),
        model_type: 'text',
        model_name: '',
        openrouter_provider: '',
        api_token: '',
        api_endpoint: '',
        api_secret: '',
        has_api_token: false,
        has_api_secret: false,
        api_region: '',
        resource_name: '',
        max_tokens: 60000,
        temperature: 0.5,
        reasoning_effort: DEFAULT_REASONING_EFFORT,
        logprobs_enabled: true,
        max_context_tokens: 128000,
        timeout: 360
      });
      if (onModelsUpdate) onModelsUpdate();
    } catch (error) {
      setIsSavingModel(false);
      openSnackbar(error?.message || t('global_setting.model_operation_error', 'Error performing operation'), 5000);
    }
  };

  const handlePerformanceChange = (field) => (event) => {
    const value = event.target.value;
    setPerformanceDefaults((prev) => {
      const next = { ...prev, [field]: value };
      const base = performanceBaseline.current;
      const dirty = Number(next.max_tokens) !== Number(base.max_tokens) ||
        Number(next.temperature) !== Number(base.temperature);
      setPerformanceDirty(dirty);
      return next;
    });
  };

  const handleSavePerformanceDefaults = () => {
    sendCommand('update_configuration', {
      max_tokens: performanceDefaults.max_tokens,
      temperature: performanceDefaults.temperature
    });
    performanceBaseline.current = performanceDefaults;
    setPerformanceDirty(false);
    openSnackbar(t('global_setting.save_completed'), 3000);
  };

  const handleDeleteModel = (model, modelType) => {
    if (window.confirm(t('global_setting.confirm_delete_model', 'Are you sure you want to delete this model configuration?'))) {
      const payload = model?.id
        ? { id: model.id }
        : { provider: model.provider, model_type: modelType };
      deleteModel(payload)
        .then((response) => {
          if (response?.status !== 'success' && response?.success !== true) {
            throw new Error(response?.message || t('global_setting.model_operation_error', 'Error performing operation'));
          }
          openSnackbar(t('global_setting.model_deleted_success', 'Model deleted successfully'), 3000);
          if (onModelsUpdate) onModelsUpdate();
        })
        .catch((error) => {
          openSnackbar(error?.message || t('global_setting.model_operation_error', 'Error performing operation'), 5000);
        });
    }
  };

  const renderModelCard = (model, modelType) => {
    const providerOption = getProviderOptionsForType(modelType).find(opt => opt.value === model.provider);
    
    return (
      <ModelCard
        key={model.id ? `${model.id}` : `${model.provider}-${modelType}`}
        model={model}
        modelType={modelType}
        providerLabel={t(`global_setting.model_label_${model.provider}`, providerOption?.label || model.provider)}
        onEdit={handleOpenDialog}
        onDelete={handleDeleteModel}
        t={t}
      />
    );
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#5757b0', mb: 3 }}>
        {t('global_setting.model_management_title', 'AI Model Management')}
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        {t('global_setting.model_management_description', 'Add and manage AI model configurations. You can have multiple configurations for each model type (text, vision, audio) and switch between them in their respective settings tabs.')}
      </Alert>

      {/* Text Models */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('global_setting.text_models', 'Text Models')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog(null, 'text')}
            sx={{ bgcolor: '#5757b0', '&:hover': { bgcolor: '#aaaaca' } }}
          >
            {t('global_setting.add_text_model', 'Add Text Model')}
          </Button>
        </Box>
        <Grid container spacing={2}>
          {savedModels.text?.length > 0 ? (
            savedModels.text.map((model) => (
              <Grid item xs={12} sm={6} md={4} key={model.id ? `${model.id}` : `${model.provider}-text`}>
                {renderModelCard(model, 'text')}
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                {t('global_setting.no_text_models', 'No text models configured yet.')}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Vision Models */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('global_setting.vision_models', 'Vision Models')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog(null, 'vision')}
            sx={{ bgcolor: '#5757b0', '&:hover': { bgcolor: '#aaaaca' } }}
          >
            {t('global_setting.add_vision_model', 'Add Vision Model')}
          </Button>
        </Box>
        <Grid container spacing={2}>
          {savedModels.vision?.length > 0 ? (
            savedModels.vision.map((model) => (
              <Grid item xs={12} sm={6} md={4} key={model.id ? `${model.id}` : `${model.provider}-vision`}>
                {renderModelCard(model, 'vision')}
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                {t('global_setting.no_vision_models', 'No vision models configured yet.')}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Audio Models */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('global_setting.audio_models', 'Audio Models')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog(null, 'audio')}
            sx={{ bgcolor: '#5757b0', '&:hover': { bgcolor: '#aaaaca' } }}
          >
            {t('global_setting.add_audio_model', 'Add Audio Model')}
          </Button>
        </Box>
        <Grid container spacing={2}>
          {savedModels.audio?.length > 0 ? (
            savedModels.audio.map((model) => (
              <Grid item xs={12} sm={6} md={4} key={model.id ? `${model.id}` : `${model.provider}-audio`}>
                {renderModelCard(model, 'audio')}
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                {t('global_setting.no_audio_models', 'No audio models configured yet.')}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Box>

      <Divider sx={{ my: 4 }} />
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
        {t('global_setting.model_defaults', 'Default model performance')}
      </Typography>
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label={t('global_setting.max_tokens', 'Max Generated Tokens')}
            value={performanceDefaults.max_tokens || ''}
            onChange={handlePerformanceChange('max_tokens')}
            fullWidth
            type="number"
            inputProps={{ min: 1 }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label={t('global_setting.temperature', 'Temperature')}
            value={performanceDefaults.temperature || ''}
            onChange={handlePerformanceChange('temperature')}
            fullWidth
            type="number"
            inputProps={{ step: 0.1, min: 0, max: 2 }}
          />
        </Grid>
        <Grid item xs={12} sx={{ textAlign: 'right' }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSavePerformanceDefaults}
            disabled={!performanceDirty}
            sx={{ mt: { xs: 1, sm: 0 } }}
          >
            {t('global_setting.save_button') || 'Save'}
          </Button>
        </Grid>
      </Grid>

      {/* Add/Edit Model Dialog */}
      <ModelDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        editingModel={editingModel}
        formData={formData}
        onFormChange={handleFormChange}
        onSave={handleSaveModel}
        getProviderOptionsForType={getProviderOptionsForType}
        providerIcons={providerIcons}
        noTokenTextModels={noTokenTextModels}
        resourceModels={resourceModels}
        isSaving={isSavingModel}
        t={t}
      />
    </Box>
  );
});

export default ModelManagement;
